import React, { use, useCallback, useEffect, useMemo } from "react";
import {
    AuthManagerContext,
    IS_POST_ONBOARDING_PREPARE_DISPLAYED,
    IS_INITIAL_WORKER_ONBOARDING,
} from "../../contexts/AuthManager";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import VStack from "../../ui_atoms/VStack";
import { useI18n } from "../../hooks/i18n-hooks";
import ListItem from "../../ui_atoms/ListItem";
import Loading from "../../components/Loading";
import { useTheme } from "../../hooks/use-theme";
import ScreenCard from "../ScreenCard";
import { usePostOnboardingData } from "../../hooks/use-postonboarding-data";
import HeaderActions from "../../components/HeaderActions";
import { StyleSheet } from "react-native";
import { useIntercom } from "../../hooks/intercom";
import { useDeleteAccount } from "../../hooks/use-delete-account";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useDeleteCurrentProfile } from "../../hooks/use-delete-current-profile";
import { CvSource, ProfileType } from "../../__generated__/graphql";
import { MenuAction } from "../user/ScreenHeader";
import { FirebaseManagerContext } from "../../contexts/FirebaseManager";
import { useFocusEffect } from "@react-navigation/core";
import { safeAsyncStorage } from "../../utils/safe-async-storage";

type PostOnboardingProfileEditorScreenProps = StackScreenProps<
    RootStackParamList,
    "PostOnboardingProfileEditor"
>;

const PostOnboardingProfileEditorScreen = ({
    navigation,
    route,
}: PostOnboardingProfileEditorScreenProps) => {
    const { newProfileId } = route.params || {};

    const i18n = useI18n();
    const styles = useStyles();
    const { colors } = useTheme();
    const { setProfile } = use(ProfileSwitcherManagerContext);
    const { user } = use(AuthManagerContext);
    const { presentIntercom } = useIntercom();
    const { signOut } = use(FirebaseManagerContext);
    const onDeleteAccount = useDeleteAccount();
    const onDeleteWorkerProfile = useDeleteCurrentProfile({
        profileId: newProfileId,
        profileType: ProfileType.Worker,
    });

    useFocusEffect(
        useCallback(() => {
            void (async () => {
                await safeAsyncStorage.setItem(IS_POST_ONBOARDING_PREPARE_DISPLAYED, "false");
            })();
        }, []),
    );

    const deleteWorkerProfile = useCallback(async () => {
        try {
            await onDeleteWorkerProfile();
            navigation.goBack();
        } catch (_e) {
            /* empty */
        }
    }, [navigation, onDeleteWorkerProfile]);

    const editCivilState = useCallback(() => {
        navigation.navigate(
            "CivilStateEdit",
            newProfileId ? { profileId: newProfileId } : undefined,
        );
    }, [navigation, newProfileId]);

    const editFiscalAttestation = useCallback(() => {
        navigation.navigate(
            "FiscalAttestationEdit",
            newProfileId ? { profileId: newProfileId } : undefined,
        );
    }, [navigation, newProfileId]);

    const editExperiences = useCallback(() => {
        navigation.navigate("ExperiencesEditModal", { profileId: newProfileId });
    }, [navigation, newProfileId]);

    const editEmail = useCallback(() => {
        // Post-onboarding is a checklist: once verified, the email step locks (the field greys out).
        navigation.navigate("EmailEdit", {
            profileId: newProfileId,
            lockWhenVerified: true,
        });
    }, [navigation, newProfileId]);

    const editHomeAddress = useCallback(() => {
        navigation.navigate(
            "HomeAddressEdit",
            newProfileId ? { profileId: newProfileId } : undefined,
        );
    }, [navigation, newProfileId]);

    const editChildren = useCallback(() => {
        navigation.navigate("ChildrenEdit", newProfileId ? { profileId: newProfileId } : undefined);
    }, [navigation, newProfileId]);

    const editBankAccount = useCallback(() => {
        navigation.navigate("BankAccountEditModal", { profileId: newProfileId });
    }, [navigation, newProfileId]);

    const {
        missingInformations,
        isValid,
        maritalStatus,
        bankAccount,
        children,
        isPartnerAndChildrenDataRequired,
        isFiscalAttestationRequired,
        hasFiscalAttestation,
        email,
        address,
        curriculumVitae,
        kycVerified,
    } = usePostOnboardingData();

    const next = useCallback(async () => {
        const isSignupFinishedDisplayed =
            (await safeAsyncStorage.getItem(IS_INITIAL_WORKER_ONBOARDING)) === "true";

        if (newProfileId) {
            if (user?.workerProfile) {
                await setProfile(user?.workerProfile);
            }
            navigation.navigate("Home", { screen: "ProfileStack" });
        } else if (isSignupFinishedDisplayed) {
            navigation.navigate("SignupFinished");
        } else {
            navigation.replace("Home", {
                screen: "MissionsScreen",
                params: { screen: "MissionList" },
            });
        }
    }, [navigation, newProfileId, setProfile, user?.workerProfile]);

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => {
                return (
                    <HeaderActions
                        actions={[
                            {
                                label: i18n.t("assistance"),
                                icon: "assistance",
                                onPress: () => {
                                    void presentIntercom();
                                },
                            },
                            ...((newProfileId
                                ? [
                                      {
                                          label: i18n.t("profile.delete.worker.profile"),
                                          icon: "trash",
                                          onPress: () => {
                                              void deleteWorkerProfile();
                                          },
                                          color: colors.error,
                                      },
                                  ]
                                : [
                                      {
                                          label: i18n.t("profile.logout_title"),
                                          icon: "logout",
                                          onPress: () => {
                                              void signOut();
                                          },
                                      },
                                      {
                                          label: i18n.t("profile.delete_account_title"),
                                          icon: "trash",
                                          onPress: () => {
                                              void safeAsyncStorage.removeItem(
                                                  IS_POST_ONBOARDING_PREPARE_DISPLAYED,
                                              );
                                              void safeAsyncStorage.removeItem(
                                                  IS_INITIAL_WORKER_ONBOARDING,
                                              );
                                              onDeleteAccount();
                                          },
                                          color: colors.error,
                                      },
                                  ]) as MenuAction[]),
                        ]}
                        style={styles.headerActions}
                    />
                );
            },
            ...(newProfileId
                ? {
                      headerTitle: i18n.t("screens.profile_editor.new_profile"),
                  }
                : {}),
        });
    }, [
        colors.error,
        deleteWorkerProfile,
        i18n,
        navigation,
        newProfileId,
        onDeleteAccount,
        onDeleteWorkerProfile,
        presentIntercom,
        styles.headerActions,
        signOut,
    ]);

    const experienceDescription = useMemo(() => {
        switch (curriculumVitae?.source) {
            case CvSource.Internal:
                return [
                    ...(curriculumVitae?.trainings || []).map((training) =>
                        i18n.t(`education_level.${training.educationLevel}`),
                    ),
                    ...(curriculumVitae?.experiences || []).map(
                        (experience) => experience.professionName,
                    ),
                ].reduce(
                    (result, value, index) => `${result}${index !== 0 ? "\n" : ""}- ${value}`,
                    "",
                );
            case CvSource.LinkedinLink:
                return curriculumVitae.link;
            case CvSource.PdfDocument:
                return i18n.t("screens.profile_editor.curriculum_vitae_pdf");
            case CvSource.Empty:
                return i18n.t("screens.profile_editor.without_qualifications");
        }
    }, [
        curriculumVitae?.experiences,
        curriculumVitae?.link,
        curriculumVitae?.source,
        curriculumVitae?.trainings,
        i18n,
    ]);

    if (!user) {
        return (
            <VStack>
                <Loading />
            </VStack>
        );
    }

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("screens.profile_editor.finalize_my_signup"),
                    onPress: next,
                    isValid: isValid,
                    mode: "secondary",
                },
            ]}
            withAssistanceButton
        >
            <ListItem
                onPress={editCivilState}
                {...(missingInformations.includes("civil_state")
                    ? {
                          description: i18n.t("screens.profile_editor.civil_state.title"),
                          labels: [
                              {
                                  text: i18n.t("screens.profile_editor.please_complete"),
                                  mode: "miniCta",
                              },
                          ],
                          icon: "civil-state",
                      }
                    : {
                          title: i18n.t("screens.profile_editor.civil_state.title"),
                          description: i18n.t(`user.${maritalStatus}`),
                          icon: "check-circle-heavy",
                          iconColor: colors.secondary,
                      })}
            />
            {isPartnerAndChildrenDataRequired && (
                <ListItem
                    onPress={editChildren}
                    {...(missingInformations.includes("children")
                        ? {
                              description: i18n.t("screens.profile_editor.children.title"),
                              labels: [
                                  {
                                      text: i18n.t("screens.profile_editor.please_complete"),
                                      mode: "miniCta",
                                  },
                              ],
                              icon: "children",
                          }
                        : {
                              title: i18n.t("screens.profile_editor.children.title"),
                              description: i18n.t("screens.profile_editor.children_number", {
                                  count: children.length,
                              }),
                              icon: "check-circle-heavy",
                              iconColor: colors.secondary,
                          })}
                />
            )}
            <ListItem
                onPress={editBankAccount}
                {...(missingInformations.includes("bank_account")
                    ? {
                          description: i18n.t("screens.profile_editor.bank_account.title"),
                          labels: [
                              {
                                  text: i18n.t("screens.profile_editor.please_complete"),
                                  mode: "miniCta",
                              },
                          ],
                          icon: "dollar-square",
                      }
                    : bankAccount
                      ? {
                            title: i18n.t("screens.profile_editor.bank_account.title"),
                            description: toIbanFormat(bankAccount.iban),
                            icon: "check-circle-heavy",
                            iconColor: colors.secondary,
                        }
                      : {})}
            />
            {isFiscalAttestationRequired && (
                <ListItem
                    onPress={editFiscalAttestation}
                    {...(missingInformations.includes("fiscal_attestation")
                        ? {
                              description: i18n.t(
                                  "screens.profile_editor.fiscal_attestation.title",
                              ),
                              labels: [
                                  {
                                      text: i18n.t("screens.profile_editor.please_complete"),
                                      mode: "miniCta",
                                  },
                              ],
                              icon: "file-lines",
                          }
                        : {
                              title: i18n.t("screens.profile_editor.fiscal_attestation.title"),
                              description: hasFiscalAttestation ? i18n.t("yes") : i18n.t("no"),
                              icon: "check-circle-heavy",
                              iconColor: colors.secondary,
                          })}
                />
            )}
            <ListItem
                onPress={editExperiences}
                {...(missingInformations.includes("experiences")
                    ? {
                          description: i18n.t("screens.profile_editor.experiences.title"),
                          labels: [
                              {
                                  text: i18n.t("screens.profile_editor.please_complete"),
                                  mode: "miniCta",
                              },
                          ],
                          icon: "award",
                      }
                    : {
                          title: i18n.t("screens.profile_editor.experiences.title"),
                          description: experienceDescription || "",
                          icon: "check-circle-heavy",
                          iconColor: colors.secondary,
                      })}
            />
            <ListItem
                onPress={editEmail}
                {...(missingInformations.includes("email")
                    ? {
                          description: i18n.t("screens.profile_editor.email.title"),
                          labels: [
                              {
                                  // A missing address has to be filled in first; one that is only
                                  // unverified just needs verifying.
                                  text: i18n.t(
                                      email
                                          ? "screens.profile_editor.email.verify"
                                          : "screens.profile_editor.please_complete",
                                  ),
                                  mode: "miniCta",
                              },
                          ],
                          icon: "private-email",
                      }
                    : {
                          title: i18n.t("screens.profile_editor.email.title"),
                          description: email ?? undefined,
                          icon: "check-circle-heavy",
                          iconColor: colors.secondary,
                      })}
            />
            {newProfileId && (
                <ListItem
                    onPress={editHomeAddress}
                    {...(missingInformations.includes("home_address")
                        ? {
                              description: i18n.t("screens.profile_editor.home_address.title"),
                              labels: [
                                  {
                                      text: i18n.t("screens.profile_editor.please_complete"),
                                      mode: "miniCta",
                                  },
                              ],
                              icon: "house",
                          }
                        : address
                          ? {
                                title: i18n.t("screens.profile_editor.home_address.title"),
                                description: address.formatted,
                                icon: "check-circle-heavy",
                                iconColor: colors.secondary,
                            }
                          : {})}
                />
            )}
            {!kycVerified && (
                <ListItem
                    onPress={() => navigation.navigate("PreIdVerification", { backOnSkip: true })}
                    {...(missingInformations.includes("kyc")
                        ? {
                              description: i18n.t("screens.profile_editor.kyc.title"),
                              labels: [
                                  {
                                      text: i18n.t("screens.profile_editor.please_complete"),
                                      mode: "miniCta",
                                  },
                              ],
                              icon: "user",
                          }
                        : kycVerified
                          ? {
                                title: i18n.t("screens.profile_editor.kyc.title"),
                                icon: "check-circle-heavy",
                                iconColor: colors.secondary,
                            }
                          : {})}
                />
            )}
        </ScreenCard>
    );
};

const useStyles = () => {
    const { gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                headerActions: {
                    marginRight: gridSize / 2,
                },
            }),
        [gridSize],
    );
};

const toIbanFormat = (iban: string) =>
    iban
        .replace(/[^\dA-Z]/g, "")
        .replace(/(.{4})/g, "$1 ")
        .trim();

export default PostOnboardingProfileEditorScreen;
